import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAuth,
  invitationService,
  LanguageSwitcher,
  logOut,
  WorkspaceInfo,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@aaas/shared';
import { LogOut } from 'lucide-react';

function PageCorner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleSignOut = async () => {
    try {
      await logOut();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    navigate('/login', { replace: true });
  };
  return (
    <div className="absolute right-4 top-4 flex items-center gap-2 z-10">
      <LanguageSwitcher />
      {user && (
        <button
          type="button"
          onClick={handleSignOut}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      )}
    </div>
  );
}

export function WorkspaceJoinPage() {
  const { workspaceCode } = useParams<{ workspaceCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, availableWorkspaces, refreshWorkspaces, switchWorkspace } = useAuth();
  const { t } = useTranslation('portal');

  const [workspace, setWorkspace] = useState<WorkspaceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    // If not logged in, redirect to login with return URL
    if (!user) {
      const currentUrl = `/join/${workspaceCode}${token ? `?token=${token}` : ''}`;
      navigate(`/login?redirect=${encodeURIComponent(currentUrl)}`);
      return;
    }

    // If no token, show error
    if (!token) {
      setError(t('workspaceJoin.errorHeading'));
      setLoading(false);
      return;
    }

    // Check if already a member
    if (availableWorkspaces) {
      const existingWorkspace = availableWorkspaces.find((w) => w.code === workspaceCode);
      if (existingWorkspace) {
        // Already a member, switch to workspace and redirect
        switchWorkspace(existingWorkspace.id);
        navigate('/', { replace: true });
        return;
      }
    }

    // Load workspace info
    loadWorkspaceInfo();
  }, [user, workspaceCode, token, availableWorkspaces]);

  const loadWorkspaceInfo = async () => {
    if (!workspaceCode) return;

    try {
      setLoading(true);
      const workspaceInfo = await invitationService.getWorkspaceByCode(workspaceCode);
      setWorkspace(workspaceInfo);
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || t('workspaceJoin.errorHeading'));
      } else {
        setError(t('workspaceJoin.errorHeading'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWorkspace = async () => {
    if (!user || !workspaceCode || !token) return;

    try {
      setJoining(true);
      setError(null);

      const idToken = await user.getIdToken();
      // Service will throw error if join fails, so if we get here it succeeded
      await invitationService.joinWorkspace(workspaceCode, { token }, idToken);

      // Refresh workspace list to include new workspace
      await refreshWorkspaces();

      // Small delay to ensure workspaces are updated
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Navigate to the workspace
      navigate('/', { replace: true });
    } catch (error: unknown) {
      console.error('Join workspace failed:', error);

      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { detail?: string; message?: string } };
        };
        setError(
          axiosError.response?.data?.message ||
            axiosError.response?.data?.detail ||
            t('workspaceJoin.errorHeading'),
        );
      } else {
        setError(t('workspaceJoin.errorHeading'));
      }
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <PageCorner />
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('workspaceJoin.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <PageCorner />
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">{t('workspaceJoin.errorHeading')}</h1>
            <p className="mt-2 text-red-600">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline" className="mt-4">
              {t('workspaceJoin.goToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <PageCorner />
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{t('workspaceJoin.heading')}</h1>
          <p className="mt-2 text-gray-600">{t('workspaceJoin.subheading')}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-3">
              {/* Workspace Avatar */}
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-lg">
                {workspace?.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-lg font-semibold">{workspace?.name}</div>
                <div className="text-sm text-gray-500">{workspace?.code}</div>
              </div>
            </CardTitle>
            <CardDescription>{t('workspaceJoin.cardDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex space-x-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate('/')}
                disabled={joining}
              >
                {t('workspaceJoin.cancelButton')}
              </Button>
              <Button className="flex-1" onClick={handleJoinWorkspace} disabled={joining}>
                {joining ? t('workspaceJoin.joiningButton') : t('workspaceJoin.joinButton')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-sm text-gray-500">{t('workspaceJoin.footer')}</p>
        </div>
      </div>
    </div>
  );
}
